import { useEffect, useSyncExternalStore } from "react";

/**
 * Admin theme (light / dark) with `system` following the OS preference —
 * mirrors the seller portal's theme behavior, minus the zustand dependency.
 *
 * The persisted preference is applied to `<html>` as a `.dark` class. A tiny
 * inline script in index.html applies it before first paint (no flash); this
 * store keeps it in sync at runtime and drives the toggle in the sidebar and
 * the mobile nav menu.
 */
export type ThemePref = "light" | "dark" | "system";

const STORAGE_KEY = "admin_theme";

function readStoredPref(): ThemePref {
  if (typeof window === "undefined") return "system";
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === "light" || value === "dark" || value === "system") {
      return value;
    }
  } catch {
    // ignore (private mode / disabled storage)
  }
  return "system";
}

function prefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

function resolveDark(pref: ThemePref): boolean {
  return pref === "dark" || (pref === "system" && prefersDark());
}

function applyToDom(pref: ThemePref): void {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", resolveDark(pref));
}

type ThemeState = {
  pref: ThemePref;
  /** True when the effective theme is dark (accounts for `system`). */
  resolvedDark: boolean;
};

let state: ThemeState = {
  pref: readStoredPref(),
  resolvedDark: resolveDark(readStoredPref()),
};

const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): ThemeState {
  return state;
}

function setState(next: ThemeState): void {
  state = next;
  listeners.forEach((listener) => listener());
}

export function setThemePref(pref: ThemePref): void {
  try {
    localStorage.setItem(STORAGE_KEY, pref);
  } catch {
    // ignore
  }
  applyToDom(pref);
  setState({ pref, resolvedDark: resolveDark(pref) });
}

export function useTheme(): ThemeState {
  return useSyncExternalStore(subscribe, getSnapshot);
}

/**
 * Applies the current preference to the DOM and keeps it in sync with OS
 * changes while the preference is `system`. Mount once near the app root.
 */
export function useApplyTheme(): void {
  const { pref } = useTheme();

  useEffect(() => {
    applyToDom(pref);
    if (pref !== "system" || typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      applyToDom("system");
      setState({ pref: "system", resolvedDark: resolveDark("system") });
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [pref]);
}
